import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PaymentSheetRequest } from '@features/patient-ledger';
import {
  useOpenLedgerDrawer,
  useOpenPaymentSheet,
} from '@features/patient-ledger';
import {
  PatientPaymentMethodChip,
  PatientPaymentMethodsBlock,
  useInvoiceDeclineRecovery,
  useLazyGetInvoiceUrlsQuery,
  useReconcileInvoice,
} from '@features/payments';
import { useSubjectLanguageVariants } from '@features/user';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';
import { useSnackbar } from 'notistack';

import type {
  EncounterLedgerGroup,
  PatientBillingStatus,
  PatientInvoiceHistoryItem,
} from '../store';
import { useLazyAccountHistoryPdfQuery, usePatientFinancialSummaryQuery } from '../store';
import { formatDateOnly } from '../utils';

import { ApplyCreditDialog } from './balance-table/ApplyCreditDialog';
import { creditAppliedMessage } from './balance-table/creditOptions';
import { AssignChargeDialog } from './balance-table/AssignChargeDialog';
import {
  RemoveRecordedPaymentDialog,
  removalMessage,
} from './balance-table/RemoveRecordedPaymentDialog';
import {
  reversalMessage,
  ReverseChargeDialog,
  type ReverseChargeTarget,
} from './balance-table/ReverseChargeDialog';
import { PatientBalanceTable } from './balance-table/PatientBalanceTable';
import { RecordRefundDialog } from './balance-table/RecordRefundDialog';
import {
  accountChargeTargetsOf,
  accountCreditSourceOf,
  balanceBreakdown,
  type BalanceRow,
  type CreditSource,
  creditTargetsOf,
  invoiceDeclineOf,
  type RowInvoiceDecline,
  toBalanceRows,
} from './balance-table/rows';
import { UpdateCardAndRetryDialog } from './balance-table/UpdateCardAndRetryDialog';
import { useBillHeldCharge } from './refund-hold/useBillHeldCharge';
import { useWriteOffHeldCharge } from './refund-hold/useWriteOffHeldCharge';
import { WriteOffHeldChargeDialog } from './refund-hold/WriteOffHeldChargeDialog';
import { formatCents, formatDeliveryMethod } from './v2/shared';
import { EncounterWhyRows } from './EncounterWhyRows';
import { openInvoiceTargeting } from '@features/billing/utils/openInvoiceTargeting';
import type { CreditSourceEntry } from './balance-table/rows';
import { hasCollectionSuppressedReconciliation } from '@features/billing/utils/collectionReviewState';

interface PatientResponsibilityPanelProps {
  patientId: string;
  patientName?: string | null;
  onBack?: () => void;
  variant?: 'default' | 'embedded';
  /** MANAGE_PATIENT_FINANCIALS: may move this patient's money —
   * collect, refund, apply credit, assign, reverse, remove a recorded
   * payment. Fed from the `record_payment` capability, which becomes
   * that permission (gdn-3287). Named for the permission rather than
   * for MANAGE_BILLING, which it is not. */
  canManagePatientFinancials?: boolean;
}

function formatDate(iso: string | null | undefined): string {
  return formatDateOnly(iso, { month: 'short', day: 'numeric', year: 'numeric' }, '—');
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    // Clipboard API is unavailable in a few embedded/browser-test contexts.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    if (!document.execCommand('copy')) {
      throw new Error('copy_command_failed');
    }
  } finally {
    document.body.removeChild(textarea);
  }
}

const STATUS_CHIP_STYLES: Record<
  PatientBillingStatus['status'],
  { bgcolor: string; color: string }
> = {
  in_queue: { bgcolor: '#F5F4FB', color: '#36229E' },
  open_invoice: { bgcolor: '#FEF3C7', color: '#92400E' },
  balance_due: { bgcolor: '#FFFBEB', color: '#D97706' },
  paid: { bgcolor: '#F0FDF4', color: '#166534' },
  credit: { bgcolor: '#ECFDF5', color: '#065F46' },
  no_activity: { bgcolor: '#F3F4F6', color: '#6B7280' },
};

export const PatientResponsibilityPanel: React.FC<PatientResponsibilityPanelProps> = ({
  patientId,
  patientName,
  onBack,
  variant = 'default',
  canManagePatientFinancials = true,
}) => {
  const {
    data,
    isLoading,
    // The assignment invalidates the summary, so the credit it banked is
    // only visible once this settles — see the deferred Apply-credit
    // offer below.
    isFetching: isSummaryFetching,
    isError: isSummaryError,
  } = usePatientFinancialSummaryQuery({ patientId });
  const openLedgerDrawer = useOpenLedgerDrawer();
  const openPaymentSheet = useOpenPaymentSheet();
  const navigate = useNavigate();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const [fetchInvoiceUrls] = useLazyGetInvoiceUrlsQuery();
  const { reconcileInvoice, isLoading: isReconcilingInvoice } = useReconcileInvoice();
  const [invoiceMenu, setInvoiceMenu] = useState<{
    anchorEl: HTMLElement;
    invoiceId: string;
  } | null>(null);
  const { singular, singularCap } = useSubjectLanguageVariants();

  const name = data?.patient_name ?? patientName ?? singularCap;
  const summary = data?.summary;
  const billingStatus = data?.billing_status;
  const balance = summary?.total_balance_cents ?? 0;
  // Oldest first, matching useLedgerData —
  // [0] is "the" open invoice everywhere and the payload does not
  // guarantee an order of its own.
  const openInvoices = (data?.invoices ?? [])
    .filter(inv => inv.status === 'open')
    .slice()
    .sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''));
  // Rows carry an invoice id; the invoice handlers want the record.
  const invoiceById = (invoiceId: string): PatientInvoiceHistoryItem | undefined =>
    (data?.invoices ?? []).find(inv => inv.invoice_id === invoiceId);
  const primaryOpenInvoice = openInvoices[0];
  /**
   * How much of the balance the open invoices already cover (gdn-3019).
   *
   * "An invoice is open" and "everything owed has been invoiced" are
   * different facts, and only the second should suppress Send Invoice.
   * Keying on the first meant a single stale invoice — including one left
   * open by a declined card — locked the button to Send Reminder forever,
   * so a patient seen again could never be invoiced for the new visit.
   *
   * Safe to offer: the send path selects only CONFIRMED charge lines and
   * marks them INVOICED, so invoicing again bills the remainder and cannot
   * re-bill what is already out.
   *
   * Deliberately a heuristic, and one that only ever errs toward the old
   * behaviour. `total_balance_cents` is net, so credits and write-offs
   * shrink it: a patient with uninvoiced CONFIRMED lines fully offset by
   * account credit reads as covered here and still gets Send Reminder.
   * That is the same answer the previous code gave in every case, so this
   * strictly widens when Send Invoice appears and never narrows it — but
   * it is not the exact question. The exact one is "are there uninvoiced
   * CONFIRMED charge lines", which the summary does not expose today
   * (gdn-3024).
   */
  const openInvoiceTotalCents = openInvoices.reduce(
    (total, invoice) => total + (invoice.amount_cents ?? 0),
    0,
  );
  const uninvoicedBalanceCents = Math.max(balance - openInvoiceTotalCents, 0);
  const hasUninvoicedBalance = uninvoicedBalanceCents > 0;

  // Real encounters only — defensively drop any orphan groups (encounter_id null)
  // even though the backend should route those into account_level_entries now.
  const realEncounters = useMemo(
    () =>
      (data?.encounters ?? []).filter(
        (enc): enc is EncounterLedgerGroup & { encounter_id: string } => enc.encounter_id != null,
      ),
    [data?.encounters],
  );
  const shouldReviewBeforeCollecting = hasCollectionSuppressedReconciliation(data?.encounters);
  const staleInvoiceIds = useMemo(() => {
    const ids: string[] = [];
    for (const enc of realEncounters) {
      const reconciliation = (
        enc as unknown as {
          patient_responsibility_reconciliation?: { stale_invoice_ids?: string[] | null } | null;
        }
      ).patient_responsibility_reconciliation;
      ids.push(...(reconciliation?.stale_invoice_ids ?? []));
    }
    return new Set(ids);
  }, [realEncounters]);

  // Map encounter_id → encounter so we can render encounter context on invoice rows
  const encountersById = useMemo(() => {
    const map = new Map<string, EncounterLedgerGroup>();
    for (const enc of realEncounters) {
      map.set(enc.encounter_id, enc);
    }
    return map;
  }, [realEncounters]);

  // Visit-breakdown "why" rows — newest visit first (GDN-2400).
  const whyRowEncounters = useMemo(
    () =>
      [...realEncounters].sort((a, b) =>
        (b.service_date ?? '').localeCompare(a.service_date ?? ''),
      ),
    [realEncounters],
  );

  // GDN-2943: the redesigned worklist table, gated. Rows derive from the
  // reconciliation read model; the legacy sections render when the gate is
  // off, so the ungated experience is untouched.
  const showBalanceTable = useFeatureGate(FeatureGates.PATIENT_BALANCE_TABLE);
  const tracking = useEventTracking();
  const accountActivityRows = data?.account_activity_rows;
  // Gross credit minus the account's own open charges — what the
  // allocation verb may actually move. Absent on older BE → the account
  // credit row offers no verb (never fall back to the gross figure).
  const accountAllocatableCreditCents =
    data?.account_allocatable_credit_cents ?? null;
  // The account's unapplied credit — allocation-aware, and the cap the
  // charge verb enforces when that credit pays an account charge
  // (gdn-4321). It can read $125 while the allocatable figure reads $0.
  const accountUnappliedCreditCents = data?.summary?.credit_balance_cents ?? null;
  const balanceRows = useMemo(
    () =>
      showBalanceTable
        ? toBalanceRows(
            realEncounters,
            data?.account_level_entries ?? [],
            accountActivityRows ?? null,
            singularCap,
            data?.invoices ?? [],
          )
        : [],
    [
      showBalanceTable,
      realEncounters,
      data?.account_level_entries,
      accountActivityRows,
      singularCap,
      data?.invoices,
    ],
  );

  // The headline's receipt: how the rows net to the balance. Null unless
  // it sums exactly — then the figure stays a plain number.
  const headlineBreakdown = useMemo(
    () => (showBalanceTable ? balanceBreakdown(balanceRows, balance) : null),
    [showBalanceTable, balanceRows, balance],
  );

  // Allocation verb (gdn-2950): mounted per open so the dialog's
  // idempotency key is fresh each time.
  const [applyCreditSource, setApplyCreditSource] = useState<CreditSource | null>(
    null,
  );
  const creditTargets = useMemo(() => creditTargetsOf(balanceRows), [balanceRows]);
  const accountChargeTargets = useMemo(
    () => accountChargeTargetsOf(balanceRows),
    [balanceRows],
  );
  const handleApplyCredit = useCallback(
    (source: CreditSource, options?: { via: 'button' | 'overflow' }) => {
      tracking.billing.patientBalanceTableActionClicked({
        encounter_id: source.sourceEncounterId ?? 'account',
        action: 'apply_credit',
        via: options?.via ?? 'button',
      });
      setApplyCreditSource(source);
    },
    [tracking],
  );

  // Refund verb (gdn-2978): payment-anchored, so the dialog fetches the
  // scope's refundable payments itself — the row only supplies the source.
  const [refundSource, setRefundSource] = useState<CreditSource | null>(null);
  const handleRecordRefund = useCallback(
    (source: CreditSource, options?: { via: 'button' | 'overflow' }) => {
      tracking.billing.patientBalanceTableActionClicked({
        encounter_id: source.sourceEncounterId ?? 'account',
        action: 'record_refund',
        via: options?.via ?? 'button',
      });
      setRefundSource(source);
    },
    [tracking],
  );

  // What the Apply-credit verb could move right now. Held in a ref
  // because the follow-on offer (gdn-3125) is clicked from a snackbar
  // that renders once: the banked figure is what the assignment put
  // there, while the ALLOCATABLE figure is a live property of the
  // account and nets against its own open charges. Offering the stale
  // number would be the display/payload divergence this table has been
  // bitten by twice.
  const accountCreditSource = useMemo(
    () =>
      accountCreditSourceOf(balanceRows, {
        allocatableCents: accountAllocatableCreditCents,
        unappliedCents: accountUnappliedCreditCents,
        visitTargets: creditTargets,
        accountCharges: accountChargeTargets,
      }),
    [
      balanceRows,
      accountAllocatableCreditCents,
      accountUnappliedCreditCents,
      creditTargets,
      accountChargeTargets,
    ],
  );
  const accountCreditSourceRef = useRef(accountCreditSource);
  accountCreditSourceRef.current = accountCreditSource;

  /** The Apply-credit follow-on, waiting on fresh data.
   *
   * Reading the ref at click time fixes the stale CLOSURE, but not a
   * stale SUMMARY: the assignment invalidates the financial summary, and
   * a biller can click the offer before that refetch renders — at which
   * point the ref still holds the account's pre-assignment credit, which
   * is precisely the number the banking just changed. Opening the dialog
   * on it would cap the allocation at a figure that is already wrong, or
   * claim nothing is movable when the credit had not landed yet.
   *
   * So the click only records intent; the effect below opens the dialog
   * once the summary has settled. Almost always that is the same tick.
   */
  const [creditOfferPending, setCreditOfferPending] = useState(false);
  useEffect(() => {
    if (!creditOfferPending || isSummaryFetching) return;
    setCreditOfferPending(false);
    // A refresh that FAILED settles isFetching just like one that
    // succeeded, leaving the pre-assignment credit in the ref. The
    // ledger would refuse an allocation drawn from it — it is the
    // eligibility authority, so no wrong money moves — but the biller
    // would meet an unexplained refusal on a dialog we opened for them.
    // Say what happened instead, and leave the row's own verb to carry
    // the repair once the account reloads.
    if (isSummaryError) {
      enqueueSnackbar(
        'Credit banked, but the account could not be refreshed — use Apply credit on the row once it reloads',
        { variant: 'warning' },
      );
      return;
    }
    const source = accountCreditSourceRef.current;
    if (source) {
      handleApplyCredit(source, { via: 'overflow' });
      return;
    }
    // Banked and allocatable can legitimately disagree — an open account
    // charge absorbs some of it, and when no charge can take it either,
    // there is nothing to move. Say so rather than opening an empty dialog.
    enqueueSnackbar("That credit covers the account's open charges", {
      variant: 'info',
    });
  }, [
    creditOfferPending,
    isSummaryFetching,
    isSummaryError,
    enqueueSnackbar,
    handleApplyCredit,
  ]);

  // Stranded-charge repair (gdn-3106): a paid account-level charge whose
  // money belonged to an encounter. Mounted per open, like the other
  // money dialogs.
  const [assignCharge, setAssignCharge] = useState<{
    chargeLineId: string;
    amountCents: number;
    label: string;
  } | null>(null);
  const [reverseCharge, setReverseCharge] = useState<ReverseChargeTarget | null>(null);
  // Bill patient / Write off on an account charge a refund left held
  // (gdn-4242). Both mutations' tags refresh this panel's summary; a row
  // has no line to show a refusal on, so it arrives as a snackbar.
  const billHeldCharge = useBillHeldCharge({
    patientId,
    surface: 'patient_balance_table',
    onRefused: message => enqueueSnackbar(message, { variant: 'error' }),
  });
  const writeOffHeldCharge = useWriteOffHeldCharge({
    patientId,
    surface: 'patient_balance_table',
  });
  const [removablePayments, setRemovablePayments] = useState<CreditSourceEntry[] | null>(
    null,
  );
  /** The credit row aggregates its constituent payments, so the biller
   * picks which one was the mistake — the handler carries the list, the
   * dialog carries the choice (gdn-3255). */
  const handleRemoveRecordedPayment = useCallback(
    (payments: CreditSourceEntry[]) => {
      tracking.billing.patientBalanceTableActionClicked({
        encounter_id: 'account',
        action: 'remove_recorded_payment',
        via: 'overflow',
      });
      setRemovablePayments(payments);
    },
    [tracking],
  );
  /** Reverse is offered on any charge-line-backed row (gdn-3187); the
   * server refuses ineligible lines with a stated reason, which the
   * dialog renders verbatim. Confirming first because the act is
   * destructive and, on a paid line, moves money. */
  const handleReverseCharge = useCallback(
    (charge: ReverseChargeTarget) => {
      // The encounter when the reversal came from a per-line expansion,
      // 'account' when it came from an account row's overflow. Hardcoding
      // 'account' was right while gdn-3187 was the only caller; since
      // gdn-3190 it would file every encounter-line reversal under the
      // account and quietly make the metric useless.
      tracking.billing.patientBalanceTableActionClicked({
        encounter_id: charge.encounterId ?? 'account',
        action: 'reverse_charge',
        via: 'overflow',
      });
      setReverseCharge(charge);
    },
    [tracking],
  );

  const handleAssignCharge = useCallback(
    (charge: { chargeLineId: string; amountCents: number; label: string }) => {
      tracking.billing.patientChargeAssignmentClicked({
        patient_id: patientId,
        amount_cents: charge.amountCents,
      });
      setAssignCharge(charge);
    },
    [patientId, tracking],
  );

  // GDN-2942: the raw ledger leaves the screen as a document — the gated
  // view's escape hatch to full per-entry history.
  const [fetchAccountHistoryPdf, { isFetching: isDownloadingHistory }] =
    useLazyAccountHistoryPdfQuery();
  const handleDownloadAccountHistory = useCallback(async () => {
    tracking.billing.patientBalanceTableDownloadHistoryClicked({
      patient_id: patientId,
    });
    try {
      const blob = await fetchAccountHistoryPdf({ patientId }).unwrap();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `account-history-${patientId}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar('Could not download account history', { variant: 'error' });
    }
  }, [tracking, fetchAccountHistoryPdf, patientId, enqueueSnackbar]);

  // Open the invoice's hosted payment page. Popup-safe: the tab opens
  // synchronously in the click handler, then navigates once the fresh URL
  // arrives (stored payment_url is the fallback if the fetch fails).
  const handleOpenInvoiceUrl = useCallback(
    async (invoiceId: string, source: 'activity_row' | 'open_invoices') => {
      tracking.billing.patientInvoiceLinkClicked({
        patient_id: patientId,
        invoice_id: invoiceId,
        source,
      });
      const fallbackUrl = (data?.invoices ?? []).find(
        inv => inv.invoice_id === invoiceId,
      )?.payment_url;
      const tab = window.open('', '_blank');
      // Sever the opener before navigating anywhere external — the invoice
      // page (or anything it redirects to) must never be able to steer this
      // tab (reverse tabnabbing; same pattern as the DoseSpot SSO windows).
      if (tab) tab.opener = null;
      let url: string | null | undefined;
      try {
        url = (await fetchInvoiceUrls({ patientId, invoiceId }).unwrap()).payment_url;
      } catch {
        url = fallbackUrl;
      }
      if (!url) {
        tab?.close();
        enqueueSnackbar('Could not open invoice', { variant: 'error' });
        return;
      }
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
    },
    [tracking, patientId, data?.invoices, fetchInvoiceUrls, enqueueSnackbar],
  );

  // GDN-2957: money actions no longer need the drawer. Gated, they open a
  // page-anchored sheet; ungated they still open the drawer, which is the
  // only surface those users have. One helper so the routing decision is
  // made once rather than at each of the six call sites.
  //
  // The two remaining drawer entries here (handleReviewBalances and the
  // balance-row fallthrough) are review destinations, not actions: Reverse
  // lives on the drawer's gated lifecycle rows and has nowhere else to go
  // until it re-homes onto the encounter view.
  const openMoneyAction = useCallback(
    (
      request: PaymentSheetRequest,
      drawerOnly?: { encounterLabel?: string | null },
    ) => {
      if (showBalanceTable) {
        openPaymentSheet(request);
        return;
      }
      openLedgerDrawer({
        patientId: request.patientId,
        patientName: request.patientName,
        encounterId: request.encounterId ?? undefined,
        encounterLabel: drawerOnly?.encounterLabel,
        contextVisitStartsAt: request.contextVisitStartsAt,
        defaultAmountCents: request.defaultAmountCents,
        chargeLineIds: request.chargeLineIds,
        initialTab: 'ledger',
        initialAction: request.action,
        source: request.source,
      });
    },
    [showBalanceTable, openPaymentSheet, openLedgerDrawer],
  );

  // GDN-2957: what remains of the drawer's "review" entries.
  //
  // Two of the three recommendations stopped being buttons entirely (see
  // balance-table/actions.ts): the credit verbs sit on the row itself, and
  // a secondary hold is a status rather than an act. `review_denial` is a
  // real verb, and it wants the claim — so it goes there.
  //
  // Ungated users keep the drawer, which is still their only surface.
  const openReviewSurface = useCallback(
    (source: string, encounterId?: string | null) => {
      if (showBalanceTable) {
        // No event here: the row path already emits
        // patientBalanceTableActionClicked before falling through, and
        // firing again would double-count every review click.
        navigate(
          encounterId
            ? `/jotbill/encounter/${encounterId}?workspace=claim`
            : `/jotbill?tab=patient-billing&patient=${patientId}`,
        );
        return;
      }
      openLedgerDrawer({
        patientId,
        patientName: name,
        initialTab: 'ledger',
        source,
      });
    },
    [showBalanceTable, navigate, patientId, name, openLedgerDrawer],
  );

  const handleOpenInvoiceReminder = useCallback(
    (invoiceId: string) => {
      const invoice = (data?.invoices ?? []).find(inv => inv.invoice_id === invoiceId);
      const encounter = invoice?.encounter_id ? encountersById.get(invoice.encounter_id) : null;
      const encounterLabel = encounter
        ? [
            formatDate(encounter.service_date),
            encounter.encounter_type_label,
            encounter.provider_name,
          ]
            .filter(Boolean)
            .join(' · ')
        : null;
      const isStale = staleInvoiceIds.has(invoiceId);

      openMoneyAction(
        {
          patientId,
          patientName: name,
          encounterId: invoice?.encounter_id ?? undefined,
          // This handler already knows exactly which invoice it means —
          // including when it chose void because that one is stale.
          invoiceId,
          defaultAmountCents: invoice?.amount_cents ?? (balance > 0 ? balance : undefined),
          action: {
            kind: 'settle',
            method: isStale ? 'void_invoice' : 'remind_invoice',
          },
          source: invoice?.encounter_id
            ? 'patient_responsibility_panel_encounter_reminder'
            : 'patient_responsibility_panel_reminder',
        },
        { encounterLabel },
      );
    },
    [balance, data?.invoices, encountersById, name, openMoneyAction, patientId, staleInvoiceIds],
  );

  const handleBalanceRowAction = useCallback(
    (row: BalanceRow, action: string, options?: { via: 'button' | 'overflow' }) => {
      tracking.billing.patientBalanceTableActionClicked({
        encounter_id: row.encounterId,
        action,
        via: options?.via ?? 'button',
      });
      if (action === 'send_invoice') {
        openMoneyAction({
          patientId,
          patientName: name,
          encounterId: row.encounterId,
          action: { kind: 'settle', method: 'invoice' },
          source: 'patient_balance_table_action',
        });
        return;
      }
      if (action === 'send_reminder') {
        const invoiceId = (data?.invoices ?? []).find(
          inv => inv.status === 'open' && inv.encounter_id === row.encounterId,
        )?.invoice_id;
        if (invoiceId) {
          handleOpenInvoiceReminder(invoiceId);
          return;
        }
      }
      // review_denial is the only review recommendation still rendered as
      // a verb; it belongs on the claim. Anything else reaching here is a
      // recommendation the table no longer surfaces as a button.
      openReviewSurface('patient_balance_table_action', row.encounterId);
    },
    [
      tracking,
      openMoneyAction,
      openReviewSurface,
      patientId,
      name,
      data?.invoices,
      handleOpenInvoiceReminder,
    ],
  );

  const selectedMenuInvoice = (data?.invoices ?? []).find(
    inv => inv.invoice_id === invoiceMenu?.invoiceId,
  );

  const handleOpenInvoiceMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, invoiceId: string) => {
      setInvoiceMenu({ anchorEl: event.currentTarget, invoiceId });
    },
    [],
  );

  const handleCloseInvoiceMenu = useCallback(() => {
    setInvoiceMenu(null);
  }, []);

  /** Takes an id, not the history record: a row's invoice can be active and
   * still absent from the 100-capped history, and requiring the record
   * would make the verb a silent no-op on exactly the rows the row-level
   * fallback exists for. The cached url is a shortcut when we have it. */
  const handleCopyInvoiceLink = useCallback(
    async (invoiceId: string, cachedPaymentUrl?: string | null) => {
      handleCloseInvoiceMenu();
      try {
        let paymentUrl: string | null | undefined;
        try {
          const result = await fetchInvoiceUrls({
            patientId,
            invoiceId,
          }).unwrap();
          paymentUrl = result.payment_url;
        } catch {
          paymentUrl = cachedPaymentUrl;
        }

        if (!paymentUrl) {
          throw new Error('missing_invoice_payment_url');
        }

        await copyTextToClipboard(paymentUrl);
        enqueueSnackbar('Invoice link copied', { variant: 'success' });
      } catch {
        enqueueSnackbar('Could not copy invoice link', { variant: 'error' });
      }
    },
    [enqueueSnackbar, fetchInvoiceUrls, handleCloseInvoiceMenu, patientId],
  );

  // Declined-invoice recovery (gdn-3064, consuming gdn-3061): retry the
  // charge against a chosen card, or switch Stripe's automatic retries
  // off. The verbs render only on rows whose invoice history record
  // carries a decline, so the record is always findable here.
  const { retryInvoicePayment, setInvoiceAutoCollection } =
    useInvoiceDeclineRecovery();
  const [retryTarget, setRetryTarget] = useState<{
    invoiceId: string;
    decline: RowInvoiceDecline;
    amountCents: number | null;
  } | null>(null);

  const handleInvoiceRetryPayment = useCallback(
    (invoiceId: string, options?: { via: 'button' | 'overflow' }) => {
      const invoice = (data?.invoices ?? []).find(
        inv => inv.invoice_id === invoiceId,
      );
      const decline = invoice ? invoiceDeclineOf(invoice) : null;
      if (!invoice || !decline) return;
      tracking.billing.patientInvoiceRetryPaymentClicked({
        patient_id: patientId,
        invoice_id: invoiceId,
        retries_exhausted: decline.nextAttemptDate === null,
        via: options?.via ?? 'overflow',
      });
      setRetryTarget({
        invoiceId,
        decline,
        amountCents: invoice.amount_cents ?? null,
      });
    },
    [data?.invoices, patientId, tracking],
  );

  const handleInvoiceStopRetries = useCallback(
    async (invoiceId: string) => {
      tracking.billing.patientInvoiceStopRetriesClicked({
        patient_id: patientId,
        invoice_id: invoiceId,
      });
      try {
        const result = await setInvoiceAutoCollection(invoiceId, false);
        enqueueSnackbar(result.message || 'Automatic retries stopped', {
          variant: 'success',
        });
      } catch (error) {
        enqueueSnackbar(
          error instanceof Error && error.message
            ? error.message
            : 'Could not stop automatic retries',
          { variant: 'error' },
        );
      }
    },
    [enqueueSnackbar, patientId, setInvoiceAutoCollection, tracking],
  );

  // Voiding kills the invoice and its payment link, but NOT the debt: the
  // BE releases every bridged charge line back to CONFIRMED, still owed
  // and re-invoiceable (reconcile_invoice → _release_charge_lines_from_
  // voided_invoice). Destructive to the collection instrument, so it
  // confirms first (gdn-3064); one confirm serves both menus. The copy
  // must never claim the balance changes — it does not.
  const [voidConfirm, setVoidConfirm] = useState<{
    invoiceId: string;
    source: 'balance_table' | 'open_invoices';
  } | null>(null);

  const handleVoidInvoice = useCallback(
    (invoiceId: string, source: 'balance_table' | 'open_invoices') => {
      handleCloseInvoiceMenu();
      tracking.billing.patientInvoiceVoidClicked({
        patient_id: patientId,
        invoice_id: invoiceId,
        source,
      });
      setVoidConfirm({ invoiceId, source });
    },
    [handleCloseInvoiceMenu, patientId, tracking],
  );

  const handleConfirmVoid = useCallback(async () => {
    if (!voidConfirm) return;
    tracking.billing.patientInvoiceVoidSubmitted({
      patient_id: patientId,
      invoice_id: voidConfirm.invoiceId,
      source: voidConfirm.source,
    });
    // useReconcileInvoice swallows failures into a snackbar and resolves
    // undefined — close only on success, so a failed void leaves the
    // confirm open for another attempt instead of silently resetting.
    const result = await reconcileInvoice(voidConfirm.invoiceId, {
      action: 'void_invoice',
      sendNotification: false,
    });
    if (result) setVoidConfirm(null);
  }, [patientId, reconcileInvoice, tracking, voidConfirm]);

  // The six legacy actions funnel through one place — openMoneyAction —
  // which routes them to the payment sheet for gated users and the Patient
  // Ledger Drawer for everyone else (gdn-2957). Patient-level actions carry
  // no encounterId; encounter-level ones do.
  const handleReviewBalances = useCallback(() => {
    openReviewSurface('patient_responsibility_panel_reconciliation_review');
  }, [openReviewSurface]);

  /** Everything owed is already on an invoice — so a second invoice would
   * double-bill, and a card charge should pay the one that exists
   * (gdn-3154). Emphasis, not visibility: collecting stays reachable. */
  // Was deliberately duplicated with the v2 PatientDetailView; that view
  // was deleted in gdn-3189, so this is the only patient-billing surface
  // and the line has no twin to keep in step with. If a second surface
  // ever needs it, hoist it rather than copying — what has to agree is
  // what reaches the sheet, which openInvoiceTargeting() already owns.
  const isFullyInvoiced = Boolean(primaryOpenInvoice) && !hasUninvoicedBalance;

  const handleSendReminder = useCallback(() => {
    if (shouldReviewBeforeCollecting) {
      handleReviewBalances();
      return;
    }
    if (primaryOpenInvoice) handleOpenInvoiceReminder(primaryOpenInvoice.invoice_id);
  }, [
    handleOpenInvoiceReminder,
    handleReviewBalances,
    primaryOpenInvoice,
    shouldReviewBeforeCollecting,
  ]);

  const handleInvoiceAction = useCallback(() => {
    // No review diversion (gdn-3175). This used to send the click to
    // Review Balances, making the collect verb a second door onto the
    // review surface — a button whose label promises collecting and
    // whose destination is something else. Review has its own button
    // beside this one now, so this one always opens the sheet.
    //
    // No defaultAmountCents here: the composer preselects the patient's
    // invoiceable charge lines, which already carry the balance. Passing the
    // balance would prefill it as a duplicate NEW ad-hoc charge (double bill).
    //
    // Opens on card_on_file rather than invoice (gdn-3107): both are the
    // `request` category, so the sheet shows the same tiles either way and
    // only the preselection differs — and the sheet already falls back to
    // invoice when no card is on file, so asking for the card here needs
    // no card-existence check of its own.
    openMoneyAction({
      patientId,
      patientName: name,
      // One field, passed whole. A spread here type-checked while
      // flattening the bundle into loose request keys — the very
      // shape gdn-3169 removes.
      openInvoiceTarget: openInvoiceTargeting(openInvoices, isFullyInvoiced),
      action: { kind: 'settle', method: 'card_on_file' },
      source: 'patient_responsibility_panel',
    });
  }, [
    isFullyInvoiced,
    name,
    // The whole array, not just primaryOpenInvoice: the count is read
    // too, and RTK Query's structural sharing can hand back the same
    // first invoice while a second one appears — leaving this closed
    // over a shorter array and reporting "pays open invoice" for a
    // patient who has two.
    openInvoices,
    openMoneyAction,
    patientId,
  ]);

  const handleAddCharges = useCallback(() => {
    openMoneyAction({
      patientId,
      patientName: name,
      action: { kind: 'add_charge' },
      source: 'patient_responsibility_panel',
    });
  }, [openMoneyAction, patientId, name]);

  const handleWriteOff = useCallback(() => {
    openMoneyAction({
      patientId,
      patientName: name,
      defaultAmountCents: balance > 0 ? balance : undefined,
      action: { kind: 'settle', method: 'write_off' },
      source: 'patient_responsibility_panel',
    });
  }, [openMoneyAction, patientId, name, balance]);

  const handleOpenLedger = useCallback(() => {
    openLedgerDrawer({
      patientId,
      patientName: name,
      initialTab: 'ledger',
      source: 'patient_responsibility_panel_ledger_button',
    });
  }, [openLedgerDrawer, patientId, name]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rounded" sx={{ height: 100 }} />
        <Skeleton variant="rounded" sx={{ height: 80 }} />
        <Skeleton variant="rounded" sx={{ height: 200 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Back link */}
      {onBack && (
        <Typography
          onClick={onBack}
          sx={{
            fontSize: '0.75rem',
            color: 'primary.main',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': { textDecoration: 'underline' },
            alignSelf: 'flex-start',
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 14 }} /> Back to Patient Billing
        </Typography>
      )}

      {/* Patient name */}
      {variant === 'default' ? (
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
          {name}
        </Typography>
      ) : null}

      {/* Attention zone — balance + status + actions */}
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          p: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                fontWeight: 600,
                color: 'text.secondary',
                letterSpacing: '0.05em',
                display: 'block',
              }}
            >
              {singularCap} Balance
            </Typography>
            {showBalanceTable ? (
              // GDN-2944: gated, the table below is the browse surface — the
              // drawer opens only in service of an action, so the balance is
              // a plain figure, not a ledger link. When the rows provably sum
              // to it, the figure hovers to its receipt.
              <Tooltip
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#fff',
                      color: 'text.primary',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 4px 16px rgba(16,24,40,0.12)',
                      p: 1.25,
                      maxWidth: 360,
                    },
                  },
                }}
                title={
                  headlineBreakdown ? (
                    <Box sx={{ minWidth: 240 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                      >
                        How this balance adds up
                      </Typography>
                      {headlineBreakdown.map((line, index) => (
                        <Box
                          key={`${line.label}-${index}`}
                          sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
                        >
                          <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                            {line.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                          >
                            {line.amountCents < 0 ? '−' : ''}
                            {formatCents(Math.abs(line.amountCents))}
                          </Typography>
                        </Box>
                      ))}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          mt: 0.5,
                          pt: 0.5,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          Balance
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                        >
                          {formatCents(balance)}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    ''
                  )
                }
              >
                <Typography
                  variant="h4"
                  // The tooltip is the only on-screen balance explanation;
                  // it opens on focus, and the breakdown is announced via
                  // the label (AT does not read tooltip content).
                  tabIndex={headlineBreakdown ? 0 : undefined}
                  aria-label={
                    headlineBreakdown
                      ? `Balance ${formatCents(balance)}. How it adds up: ${headlineBreakdown
                          .map(
                            line =>
                              `${line.label}, ${line.amountCents < 0 ? 'minus ' : ''}${formatCents(
                                Math.abs(line.amountCents),
                              )}`,
                          )
                          .join('; ')}.`
                      : undefined
                  }
                  sx={{
                    mt: 0.5,
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: '2rem',
                    width: 'fit-content',
                    color:
                      balance > 0 ? '#D97706' : balance < 0 ? '#16A34A' : 'text.primary',
                    ...(headlineBreakdown && {
                      borderBottom: '1px dotted',
                      borderColor: 'text.disabled',
                      cursor: 'help',
                    }),
                  }}
                >
                  {formatCents(balance)}
                </Typography>
              </Tooltip>
            ) : (
              <Button
                variant="text"
                onClick={handleOpenLedger}
                aria-label={`View ${singularCap.toLowerCase()} balance ledger`}
                sx={{
                  mt: 0.5,
                  p: 0,
                  minWidth: 0,
                  color:
                    balance > 0 ? '#D97706' : balance < 0 ? '#16A34A' : 'text.primary',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    bgcolor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                <Typography
                  component="span"
                  variant="h4"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: '2rem',
                    color: 'inherit',
                  }}
                >
                  {formatCents(balance)}
                </Typography>
              </Button>
            )}
            {billingStatus ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={billingStatus.status_label}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    borderRadius: '11px',
                    ...STATUS_CHIP_STYLES[billingStatus.status],
                  }}
                />
                {/* gdn-3000: the card's home under the gate. Ungated it
                    still has its own section in the band below. */}
                {showBalanceTable && <PatientPaymentMethodChip patientId={patientId} />}
              </Box>
            ) : (
              <Skeleton
                variant="rounded"
                sx={{ width: 90, height: 22, mt: 1, borderRadius: '11px' }}
              />
            )}
          </Box>

          {canManagePatientFinancials ? (
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{ flexWrap: 'wrap', alignItems: 'center' }}
            >
              {/* Record Payment is gone (gdn-3154): recording cash and
                  charging a card are the same errand — "collect money from
                  this patient" — and splitting them made the biller
                  classify their intent before the sheet would show the
                  right verbs. That question is the one the product owner
                  got wrong twice. The sheet now offers both, sectioned,
                  and each submit names its own consequence. */}
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddCharges}
                sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
              >
                Add Charges
              </Button>
              {balance > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleWriteOff}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                >
                  Write Off
                </Button>
              )}
              {/* No balance guard (gdn-3096): a patient who has not been
                  seen has no balance, and that is precisely when a deposit
                  or prepayment gets invoiced — the composer already
                  supports the pure ad-hoc invoice. Write Off keeps its
                  guard above: writing off nothing is meaningless. */}
              {/* Collect payment is ALWAYS present (gdn-3154). The
                  gdn-3019 guard used to decide whether it existed; now it
                  only decides which act leads. A fully-invoiced patient
                  still needs a way to log the cash they just handed over,
                  which is what the deleted Record Payment button used to
                  provide in exactly this state. */}
              {shouldReviewBeforeCollecting ? (
                /* Review leads; it does not hide (gdn-3175). "Deal with
                   the credit or the denial first" is correct bookkeeping,
                   and the sheet now carries that advice as a suggestion.
                   Removing the verb does not: the biller holding cash a
                   patient just handed over still has to record it, and a
                   header with no collect verb sent them hunting. This was
                   the last visibility suppression left after gdn-3154. */
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleReviewBalances}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    Review Balances
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleInvoiceAction}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    Collect payment
                  </Button>
                </>
              ) : (
                <>
                  {isFullyInvoiced && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSendReminder}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                    >
                      Send Reminder
                    </Button>
                  )}
                  <Button
                    variant={isFullyInvoiced ? 'outlined' : 'contained'}
                    size="small"
                    onClick={handleInvoiceAction}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    Collect payment
                  </Button>
                </>
              )}
            </Stack>
          ) : null}
        </Box>
      </Box>

      {/* Ungated only. Under the gate the payment method rides the header
          chip (gdn-3000) and open invoices are row state on the table
          below (gdn-3001), so this whole band would say everything twice. */}
      {!showBalanceTable && (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {/* Left — payment methods */}
        <PatientPaymentMethodsBlock patientId={patientId} defaultCollapsed={false} />

        {/* Right — open invoices */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '10px',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
              Open Invoices
            </Typography>
            {openInvoices.length > 0 && (
              <Chip
                label={openInvoices.length}
                size="small"
                sx={{ height: 18, fontSize: '0.625rem', fontWeight: 600 }}
              />
            )}
          </Box>
          {openInvoices.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                No open invoices
              </Typography>
            </Box>
          ) : (
            openInvoices.map((inv: PatientInvoiceHistoryItem, idx: number) => {
              const linkedEnc = inv.encounter_id ? encountersById.get(inv.encounter_id) : null;
              const encounterContext = linkedEnc
                ? [
                    formatDate(linkedEnc.service_date),
                    linkedEnc.encounter_type_label,
                    linkedEnc.provider_name,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : 'Account-level';
              return (
                <Box
                  key={inv.invoice_id}
                  onClick={() => void handleOpenInvoiceUrl(inv.invoice_id, 'open_invoices')}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      void handleOpenInvoiceUrl(inv.invoice_id, 'open_invoices');
                    }
                  }}
                  role="link"
                  tabIndex={0}
                  aria-label="Open invoice"
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderBottom: idx < openInvoices.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.875rem' }}
                    >
                      {formatCents(inv.amount_cents)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}
                    >
                      Sent {formatDate(inv.created_at)}
                      {inv.delivery_method
                        ? ` via ${formatDeliveryMethod(inv.delivery_method)}`
                        : ''}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <IconButton
                      aria-label="Invoice actions"
                      size="small"
                      onClick={event => {
                        event.stopPropagation();
                        handleOpenInvoiceMenu(event, inv.invoice_id);
                      }}
                      onKeyDown={event => event.stopPropagation()}
                      disabled={isReconcilingInvoice}
                      sx={{ color: 'text.secondary' }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6875rem',
                      color: 'text.secondary',
                      display: 'block',
                      mt: 0.25,
                      fontStyle: linkedEnc ? 'normal' : 'italic',
                    }}
                  >
                    {encounterContext}
                  </Typography>
                </Box>
              );
            })
          )}
          <Menu
            anchorEl={invoiceMenu?.anchorEl ?? null}
            open={Boolean(invoiceMenu)}
            onClose={handleCloseInvoiceMenu}
          >
            {selectedMenuInvoice && (
              <>
                <MenuItem
                  onClick={() =>
                    void handleCopyInvoiceLink(
                      selectedMenuInvoice.invoice_id,
                      selectedMenuInvoice.payment_url,
                    )
                  }
                >
                  Copy invoice link
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleCloseInvoiceMenu();
                    handleOpenInvoiceReminder(selectedMenuInvoice.invoice_id);
                  }}
                >
                  Send reminder
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    handleVoidInvoice(selectedMenuInvoice.invoice_id, 'open_invoices')
                  }
                  disabled={isReconcilingInvoice}
                  sx={{ color: 'error.main' }}
                >
                  Void invoice
                </MenuItem>
              </>
            )}
          </Menu>
        </Box>
      </Box>

      )}

      {/* GDN-2943 (gated): the reconciliation worklist table — rendered even
          with zero encounters so its empty state shows. Ungated: the
          per-encounter "why" rows (GDN-2400), unchanged. */}
      {showBalanceTable && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
              Activity
            </Typography>
            <Button
              size="small"
              variant="text"
              disabled={isDownloadingHistory}
              onClick={handleDownloadAccountHistory}
              sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
            >
              {isDownloadingHistory ? 'Preparing…' : 'Download account history'}
            </Button>
          </Box>
          <PatientBalanceTable
            rows={balanceRows}
            onAction={handleBalanceRowAction}
            onFilterSelected={filter =>
              tracking.billing.patientBalanceTableFilterSelected({ filter })
            }
            subjectCap={singularCap}
            // The unapplied credit a charge on the account can take
            // (gdn-4321), beside the allocatable figure a visit can take.
            accountUnappliedCreditCents={accountUnappliedCreditCents}
            // Every verb that bills, collects, voids or charges rides the
            // capability (gdn-3287 slice 2). Copy invoice link does NOT,
            // and that asymmetry is a decision rather than an oversight:
            // it moves no money and changes no state — it hands the biller
            // a URL they are already looking at the invoice for. Gating it
            // would withhold information, not authority.
            onAccountInvoiceReminder={
              canManagePatientFinancials ? handleOpenInvoiceReminder : undefined
            }
            onInvoiceCopyLink={invoiceId =>
              void handleCopyInvoiceLink(invoiceId, invoiceById(invoiceId)?.payment_url)
            }
            onInvoiceVoid={
              canManagePatientFinancials
                ? invoiceId => handleVoidInvoice(invoiceId, 'balance_table')
                : undefined
            }
            onInvoiceRetryPayment={
              canManagePatientFinancials ? handleInvoiceRetryPayment : undefined
            }
            onInvoiceStopRetries={
              canManagePatientFinancials
                ? invoiceId => void handleInvoiceStopRetries(invoiceId)
                : undefined
            }
            onAccountInvoiceOpen={invoiceId =>
              void handleOpenInvoiceUrl(invoiceId, 'activity_row')
            }
            // All five row verbs ride one capability, the same one the
            // header verbs ride (gdn-3287). Withholding the HANDLER is how
            // the table withholds the verb: every one is gated on its
            // handler's presence — the credit pair inside
            // applyCreditFor/recordRefundFor, the other three on
            // Boolean(onX) — so undefined means absent, not
            // present-and-dead.
            //
            // Before this, a view-only user since the Sept 1 gate flip was
            // offered Apply credit, Refund and Assign charge, all of which
            // sit on billing-admin endpoints: the dialog opened, they
            // filled it in, and the confirm failed.
            // Handler-presence gating covers the verbs the panel owns, but
            // send_invoice / send_reminder are offered from the row's
            // recommended_action, which no handler prop can withhold. The
            // table needs the capability itself to drop those (gdn-3287).
            canManagePatientFinancials={canManagePatientFinancials}
            onApplyCredit={canManagePatientFinancials ? handleApplyCredit : undefined}
            onRecordRefund={canManagePatientFinancials ? handleRecordRefund : undefined}
            onAssignCharge={canManagePatientFinancials ? handleAssignCharge : undefined}
            onReverseCharge={canManagePatientFinancials ? handleReverseCharge : undefined}
            onRemoveRecordedPayment={
              canManagePatientFinancials ? handleRemoveRecordedPayment : undefined
            }
            onBillHeldCharge={
              canManagePatientFinancials
                ? charge => void billHeldCharge.billPatient({ ...charge, code: null })
                : undefined
            }
            onWriteOffHeldCharge={
              canManagePatientFinancials
                ? charge => writeOffHeldCharge.openWriteOff({ ...charge, code: null })
                : undefined
            }
            accountAllocatableCreditCents={accountAllocatableCreditCents}
            onRowClicked={encounterId => {
              const row = balanceRows.find(
                r => r.kind === 'encounter' && r.encounterId === encounterId,
              );
              tracking.billing.patientBalanceTableRowClicked({
                encounter_id: encounterId,
                bucket: row?.kind === 'encounter' ? row.bucket : 'unknown',
              });
            }}
          />
          {/* Mounted once and toggled: MUI transitions need it to stay. */}
          <WriteOffHeldChargeDialog {...writeOffHeldCharge.dialog} />
          {applyCreditSource && (
            <ApplyCreditDialog
              patientId={patientId}
              source={applyCreditSource}
              targets={creditTargets}
              accountCharges={accountChargeTargets}
              onClose={() => setApplyCreditSource(null)}
              // The dialog records the confirm itself, from every entry point.
              onApplied={applied => {
                setApplyCreditSource(null);
                enqueueSnackbar(creditAppliedMessage(applied), { variant: 'success' });
              }}
            />
          )}
          {retryTarget && (
            <UpdateCardAndRetryDialog
              patientId={patientId}
              decline={retryTarget.decline}
              amountCents={retryTarget.amountCents}
              onClose={() => setRetryTarget(null)}
              onSubmitted={payload =>
                tracking.billing.patientInvoiceRetryPaymentSubmitted({
                  patient_id: patientId,
                  invoice_id: retryTarget.invoiceId,
                  payment_method_changed: payload.payment_method_changed,
                })
              }
              onRetry={async paymentMethodId => {
                // status/message are the outcome of THIS request; the
                // failure count/reason on the response are webhook-lagged
                // history and deliberately unread here.
                const result = await retryInvoicePayment(
                  retryTarget.invoiceId,
                  paymentMethodId,
                );
                enqueueSnackbar(result.message, {
                  variant: result.status === 'paid' ? 'success' : 'info',
                });
              }}
            />
          )}
          {removablePayments && (
            <RemoveRecordedPaymentDialog
              payments={removablePayments}
              onClose={() => setRemovablePayments(null)}
              onRemoved={result => {
                setRemovablePayments(null);
                // Report reopened charges from the server without implying a refund.
                enqueueSnackbar(removalMessage(result), { variant: 'success' });
              }}
            />
          )}
          {reverseCharge && (
            <ReverseChargeDialog
              charge={reverseCharge}
              onClose={() => setReverseCharge(null)}
              onReversed={reversedCents => {
                setReverseCharge(null);
                // Shared with the dialog so the confirmation and its
                // aftermath cannot drift; it is also the only continuity
                // the biller gets, since the reversed line vanishes on
                // refetch (the server omits VOID lines by design).
                enqueueSnackbar(
                  reversalMessage(reverseCharge.hasAppliedPayment, reversedCents),
                  { variant: 'success' },
                );
              }}
            />
          )}
          {assignCharge && (
            <AssignChargeDialog
              patientId={patientId}
              charge={assignCharge}
              subject={singular}
              // Reconciled, owing, non-hold encounters — the same
              // derivation Apply credit targets, and the only set that
              // can receive money. Newest first: order, never selection.
              targets={creditTargets}
              onClose={() => setAssignCharge(null)}
              onSubmitted={payload =>
                tracking.billing.patientChargeAssignmentSubmitted({
                  patient_id: patientId,
                  target_encounter_id: payload.target_encounter_id,
                  amount_cents: assignCharge.amountCents,
                  assigned_cents: payload.assigned_cents,
                  credited_cents: payload.credited_cents,
                  has_note: payload.has_note,
                })
              }
              onAssigned={payload => {
                setAssignCharge(null);
                const banked = payload.credited_cents > 0;
                enqueueSnackbar(
                  banked
                    ? `Assigned ${formatCents(payload.assigned_cents)} to the encounter · ${formatCents(payload.credited_cents)} banked as credit`
                    : `Assigned ${formatCents(payload.assigned_cents)} to the encounter`,
                  {
                    variant: 'success',
                    // Offered, never opened for them: which visit the
                    // banked credit belongs to is the same judgement the
                    // assignment itself refuses to guess.
                    action: banked
                      ? key => (
                          <Button
                            size="small"
                            // Inherits the snackbar variant's contrast
                            // colour rather than pinning one, so the
                            // theme stays the authority on it.
                            color="inherit"
                            sx={{ fontWeight: 700 }}
                            onClick={() => {
                              closeSnackbar(key);
                              setCreditOfferPending(true);
                            }}
                          >
                            Apply credit
                          </Button>
                        )
                      : undefined,
                  },
                );
              }}
            />
          )}
          {refundSource && (
            <RecordRefundDialog
              patientId={patientId}
              source={refundSource}
              onClose={() => setRefundSource(null)}
              onSubmitted={payload =>
                tracking.billing.patientRefundSubmitted({
                  patient_id: patientId,
                  amount_cents: payload.amount_cents,
                  payment_ledger_entry_id: payload.payment_ledger_entry_id,
                  method: payload.method,
                  source: refundSource.kind,
                })
              }
              onRecorded={payload => {
                setRefundSource(null);
                enqueueSnackbar(
                  `Recorded a ${formatCents(payload.amount_cents)} refund`,
                  { variant: 'success' },
                );
              }}
            />
          )}
        </Box>
      )}
      {!showBalanceTable && whyRowEncounters.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ fontSize: '0.9375rem', fontWeight: 600, mb: 1.5 }}>
            Visit Breakdown
          </Typography>
          <EncounterWhyRows encounters={whyRowEncounters} />
        </Box>
      )}

      {/* Account-level activity — write-offs / charges not tied to a visit.
          Gated: these entries interleave into the balance table instead. */}
      {!showBalanceTable && data?.account_level_entries && data.account_level_entries.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ fontSize: '0.9375rem', fontWeight: 600, mb: 1.5 }}>
            Account Activity
          </Typography>
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '10px',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            {data.account_level_entries.map((entry, idx, arr) => (
              <Box
                key={entry.entry_id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2.5,
                  py: 1.25,
                  borderBottom: idx < arr.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.6875rem', color: 'text.secondary', minWidth: 70 }}
                >
                  {formatDate(entry.created_at)}
                </Typography>
                <Chip
                  label={entry.entry_type_label}
                  size="small"
                  sx={{ height: 18, fontSize: '0.5625rem', fontWeight: 600 }}
                />
                <Typography variant="body2" sx={{ flex: 1, fontSize: '0.75rem' }}>
                  {entry.description ?? ''}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: entry.amount_cents < 0 ? '#16A34A' : '#D97706',
                  }}
                >
                  {entry.amount_cents < 0 ? '−' : ''}
                  {formatCents(Math.abs(entry.amount_cents))}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Voiding closes the invoice, not the debt: the charges it billed
          go back to uninvoiced and stay owed. Both confusions get named —
          it is not a pause on collection, and it is not forgiveness
          (that is Write Off). */}
      <Dialog
        open={voidConfirm !== null}
        onClose={() => setVoidConfirm(null)}
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Void this invoice?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Voiding closes this invoice and its payment link for good. The
            charges it billed stay on the account — back to uninvoiced,
            still owed, ready to invoice again or write off. It does not
            reduce what the {singular} owes, and it is not a pause on
            collection.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setVoidConfirm(null)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Keep the bill
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isReconcilingInvoice}
            onClick={() => void handleConfirmVoid()}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Void invoice
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
